import React, { useEffect } from 'react';
import qs from 'qs';
import { orderBy, groupBy, uniq, maxBy, omit, isEmpty, sum, sumBy, get, keyBy, omitBy, pick, isUndefined, max } from 'lodash';
import { format as formatDate, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval, addDays, addMonths, startOfDay, endOfDay, startOfMonth, endOfMonth, differenceInDays } from 'date-fns';
import { Button, Badge, Nav, NavItem, NavLink, } from 'reactstrap';
import numeral from 'numeral';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line, } from 'recharts';
import ellipsis from 'text-ellipsis';

import { currencies, prefectures, routes, colors, } from '../shared/config';
import { chart, } from '../shared/models/order';
import useQueryParams from './hooks/useQueryParams';
import QuerySelector from './QuerySelector';

const { entries } = Object;
const { metrics, metricOptions, dateTypes, dateTypeOptions, } = chart;

export default function TrendCharts(props) {
  const { filteredRows, startOn, endOn, productsById, activeProductTypes } = props;
  const dimensions = props.dimensions ?? chart.dimensions;
  const xAxisUnits = {
    daily: {
      label: '日次',
      xs: _ => eachDayOfInterval({ start: _.startOn, end: _.endOn }).map(_ => [_]),
      format: _ => formatDate(_[0], 'yyyy/MM/dd'),
    },
    weekly: {
      label: '週次',
      xs: _ => eachWeekOfInterval({ start: _.startOn, end: _.endOn }).map(_ => eachDayOfInterval({ start: _, end: addDays(_, 6) })),
      format: _ => `${formatDate(_[0], 'yyyy/MM/dd')}〜`,
    },
    monthly: {
      label: '月次',
      xs: _ => eachMonthOfInterval({ start: _.startOn, end: _.endOn }).map(_ => eachDayOfInterval({ start: _, end: endOfMonth(_) })),
      format: _ => formatDate(_[0], 'yyyy/MM'),
    },
  };
  const xAxisUnitOptions = entries(xAxisUnits).map(([k, v]) => ({ label: v.label, value: k, }));
  const queryParams = useQueryParams();
  const {
    xAxisUnit = 'daily',
    metric = 'count',
  } = queryParams;
  const xGroupedRows = groupBy(filteredRows, _ => formatDate(_.order.createdAt.toDate(), 'yyyyMMdd'));
  const xs = xAxisUnits[xAxisUnit].xs({ startOn, endOn });
  const rowGroups = xs.map((dates) => {
    const rows = dates.flatMap(_ => xGroupedRows[formatDate(_, 'yyyyMMdd')] || []);
    return {
      dates,
      rows,
    };
  });
  const cardWidth = 600;
  const cardHeight = cardWidth * 0.8;
  const chartWidth = cardWidth * 0.95;
  const chartHeight = chartWidth * 0.7;
  const metricsWithFilter = metrics(productsById, activeProductTypes);
  const charts = {
    all: {
      title: '全体',
      data: rowGroups.map(_ => ({
        x: xAxisUnits[xAxisUnit].format(_.dates),
        [metricsWithFilter[metric].label]: metricsWithFilter[metric].value(_.rows),
      })),
      ys: [
        { label: metricsWithFilter[metric].label, },
      ],
    },
    ...entries(dimensions).reduce((x, [dimension, { label, computeItems, value, }]) => {
      const items = computeItems(filteredRows);
      const data = rowGroups.map((rowGroup) => {
        const { dates, rows } = rowGroup;
        const rowsGroupedByDimension = groupBy(rows, value);
        return {
          x: xAxisUnits[xAxisUnit].format(dates),
          ...items.reduce((x, item) => {
            const rows = rowsGroupedByDimension[item.value] || [];
            return {
              ...x,
              [item.label]: metricsWithFilter[metric].value(rows),
            };
          }, {}),
        };
      });
      return {
        ...x,
        [dimension]: {
          title: label,
          data: data,
          ys: items,
        },
      };
    }, {}),
  };

  return (
    <div className="mt-3">
      <div className='d-flex align-items-end gap-2 justify-content-end'>
        <QuerySelector
          paramName='xAxisUnit'
          options={xAxisUnitOptions}
          label=''
          defaultValue={'daily'}
          isClearable={false}
        />
        <QuerySelector
          paramName='metric'
          options={metricOptions(productsById, activeProductTypes)}
          label=''
          defaultValue={'bodysCount'}
          isClearable={false}
        />
      </div>
      <div className="mt-3 d-flex flex-wrap justify-content-around gap-3">
        {
          entries(charts).map(([name, { title, data, ys, }]) => {
            return (
              <div key={name} className="card" style={{ width: cardWidth, height: cardHeight }}>
                <div className="card-header d-flex justify-content-between">
                  <div className="flex-fill">
                    {title}
                  </div>
                </div>
                <div className="card-body d-flex justify-content-center align-items-center">
                  <div>
                    <LineChart width={chartWidth} height={chartHeight} data={data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="x" />
                      <YAxis tick={{ fontSize: 14.5 }} tickFormatter={_ => numeral(_).format(metricsWithFilter[metric].format())} tick={{ fontSize: 14.5 }} />
                      <Tooltip />
                      <Legend />
                      {
                        ys.map(({ label }, i) => {
                          return (
                            <Line key={label} dataKey={label} type="monotone" dataKey={label} fill={colors[i]} stroke={colors[i]} strokeWidth={2} formatter={(v, _, { payload }) => `${numeral(v).format(metricsWithFilter[metric].format())} (${numeral(v / sum(Object.values(omit(payload, 'x')))).format('0,0.0%')})`} />
                          );
                        })
                      }
                    </LineChart>
                  </div>
                </div>
              </div>
            )
          })
        }
      </div>
    </div>
  );
}

